import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix broken default Leaflet icon URLs in webpack builds
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl:       require('leaflet/dist/images/marker-icon.png'),
  shadowUrl:     require('leaflet/dist/images/marker-shadow.png'),
});

// ─── Category colours ─────────────────────────────────────────────────────────
export const CATEGORY_COLOR = {
  cultural_heritage:  '#a855f7',
  ecological_zone:    '#16a34a',
  resource_area:      '#ea580c',
  sacred_site:        '#eab308',
  seasonal_territory: '#0891b2',
  governance_area:    '#2563eb',
  other:              '#94a3b8',
};

export const CATEGORY_LABEL = {
  cultural_heritage:  'Cultural Heritage',
  ecological_zone:    'Ecological Zone',
  resource_area:      'Resource Area',
  sacred_site:        'Sacred Site',
  seasonal_territory: 'Seasonal Territory',
  governance_area:    'Governance Area',
  other:              'Other',
};

export const CATEGORY_ICON = {
  cultural_heritage:  '🏛️',
  ecological_zone:    '🌿',
  resource_area:      '🎣',
  sacred_site:        '✨',
  seasonal_territory: '🍂',
  governance_area:    '🗺️',
  other:              '📍',
};

// ─── Custom icon factory ──────────────────────────────────────────────────────
function makeIcon(emoji, color) {
  return L.divIcon({
    className:   '',
    html:        `<div style="font-size:20px;line-height:1;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.6));text-shadow:0 0 8px ${color};">${emoji}</div>`,
    iconSize:    [24, 24],
    iconAnchor:  [12, 12],
    popupAnchor: [0, -14],
  });
}

// ─── Auto-fit bounds ──────────────────────────────────────────────────────────
function BoundsUpdater({ values }) {
  const map = useMap();
  useEffect(() => {
    const points = [];
    values.forEach(v => {
      if (!v.geometry) return;
      if (v.geometry.type === 'Point') {
        const [lng, lat] = v.geometry.coordinates;
        points.push([lat, lng]);
      } else if (v.geometry.type === 'Polygon') {
        v.geometry.coordinates[0]?.forEach(([lng, lat]) => points.push([lat, lng]));
      } else if (v.geometry.type === 'LineString') {
        v.geometry.coordinates.forEach(([lng, lat]) => points.push([lat, lng]));
      }
    });
    if (points.length > 0) {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 13 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);
  return null;
}

// ─── Status badge colors ──────────────────────────────────────────────────────
const STATUS_DOT = { draft: '#94a3b8', verified: '#22c55e', disputed: '#f59e0b', archived: '#475569' };

// ─── LandValueMap ─────────────────────────────────────────────────────────────
/**
 * Props:
 *   values       — array of LandValue documents
 *   center       — [lat, lng]  (default: [56, -120])
 *   zoom         — number      (default: 5)
 *   onValueClick — (value) => void   (optional)
 *   height       — CSS string (default: '400px')
 */
function LandValueMap({
  values = [],
  center = [56, -120],
  zoom   = 5,
  onValueClick,
  height = '400px',
}) {
  return (
    <div style={{ height, borderRadius: 10, overflow: 'hidden', border: '1px solid #1e293b' }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%', background: '#0f172a' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
        />
        <BoundsUpdater values={values} />

        {values.map((v) => {
          if (!v.geometry) return null;
          const color = CATEGORY_COLOR[v.category] || '#94a3b8';
          const icon  = CATEGORY_ICON[v.category]  || '📍';
          const label = CATEGORY_LABEL[v.category] || v.category;
          const statusDot = STATUS_DOT[v.status] || '#94a3b8';

          const popup = (
            <Popup>
              <div style={{ minWidth: 180 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{v.name}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                  {icon} {label}
                  <span style={{ marginLeft: 6, color: statusDot }}>● {v.status}</span>
                </div>
                {v.description && (
                  <p style={{ fontSize: 12, margin: '4px 0' }}>{v.description}</p>
                )}
                {v.tenantId?.name && (
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    Community: {v.tenantId.name}
                  </div>
                )}
                {onValueClick && (
                  <button
                    onClick={() => onValueClick(v)}
                    style={{ marginTop: 8, background: color, border: 'none', color: '#fff',
                      borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}
                  >
                    Details
                  </button>
                )}
              </div>
            </Popup>
          );

          if (v.geometry.type === 'Point') {
            const [lng, lat] = v.geometry.coordinates;
            return (
              <Marker key={v._id} position={[lat, lng]} icon={makeIcon(icon, color)}>
                {popup}
              </Marker>
            );
          }

          if (v.geometry.type === 'Polygon') {
            const positions = v.geometry.coordinates[0]?.map(([lng, lat]) => [lat, lng]) || [];
            return (
              <Polygon
                key={v._id}
                positions={positions}
                pathOptions={{ color, fillColor: color, fillOpacity: 0.25, weight: 2 }}
              >
                {popup}
              </Polygon>
            );
          }

          if (v.geometry.type === 'LineString') {
            const positions = v.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
            return (
              <Polyline
                key={v._id}
                positions={positions}
                pathOptions={{ color, weight: 3, opacity: 0.8 }}
              >
                {popup}
              </Polyline>
            );
          }

          return null;
        })}
      </MapContainer>
    </div>
  );
}

export default LandValueMap;
